import { useState, useEffect } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Eye, EyeOff, ArrowLeft, Car, GraduationCap, Users, Gift, Check, ExternalLink } from "lucide-react";
import { DarkModeToggle } from "../components/DarkModeToggle";
import axios from "axios";
import { trackFunnel } from "../utils/analytics";
import { formatApiError } from "../utils/apiError";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Register() {
  const { referralCode: urlReferralCode } = useParams();
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get("invite_code") || "";
  const organisationInviteCode = (searchParams.get("organisation_invite") || "").trim().toUpperCase();
  const betaCodeFromUrl = (searchParams.get("beta_code") || "").trim().toUpperCase();
  const [betaCode, setBetaCode] = useState(betaCodeFromUrl);
  const [privateMode, setPrivateMode] = useState(false);
  const [organisationInvite, setOrganisationInvite] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("learner");
  const [learnerEntryIntent, setLearnerEntryIntent] = useState(
    inviteCode ? "has_instructor" : ""
  );
  const [referralCode, setReferralCode] = useState(urlReferralCode || "");
  const [referrerName, setReferrerName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [acceptCancellation, setAcceptCancellation] = useState(false);
  const { register, refreshUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (urlReferralCode) {
      validateReferral(urlReferralCode);
    }
  }, [urlReferralCode]);

  // Funnel: signup viewed
  useEffect(() => { trackFunnel("signup", "viewed", { role }); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // Detect private-mode so the UI can require + display the beta-code field.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/api/system/mode`)
      .then(({ data }) => { if (!cancelled) setPrivateMode(!!data?.private); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!organisationInviteCode) return undefined;
    let cancelled = false;
    axios.get(`${API_URL}/api/organisation/invites/${organisationInviteCode}/preview`)
      .then(({ data }) => {
        if (cancelled) return;
        setOrganisationInvite(data);
        setRole("pdi");
        if (data?.name) setName(data.name);
        if (data?.email) setEmail(data.email);
      })
      .catch((err) => {
        if (!cancelled) setError(formatApiError(err, "This PDI invitation is no longer available."));
      });
    return () => { cancelled = true; };
  }, [organisationInviteCode]);

  const validateReferral = async (code) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/referral/validate/${code}`);
      if (data.valid) {
        setReferrerName(data.referrer_name);
        setReferralCode(code);
      }
    } catch {
      // Invalid code - ignore
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (role === "learner" && !inviteCode && !learnerEntryIntent) {
      setError("Tell us whether you already have an instructor, want to find one, or want to learn independently for now.");
      return;
    }

    if (password.length < 10) {
      setError("Password must be at least 10 characters, with upper, lower, number, and symbol");
      return;
    }

    if (!acceptTerms || !acceptPrivacy || !acceptCancellation) {
      setError("You must accept all policies to create an account");
      return;
    }

    if (privateMode && !betaCode.trim() && !inviteCode) {
      if (organisationInviteCode) {
        // A directed PDI invitation is also valid private-beta authority.
      } else {
      // Bug fix (Feb 2026) — Invite Authority > Beta Gate.
      // A valid `invite_code` query param (from the
      // /invite/:code → onboard success → Create Account flow)
      // IS the access authority. The backend already accepts a
      // work_students invite_code as a private-mode bypass (see
      // `backend/routes/auth.py` ~line 163). The frontend must
      // not block here OR force the user back into the public
      // BETA-XXXXXXXX gate. Doctrine: valid invite > beta gate.
      setError("This is a private beta — please enter your invite code (e.g. BETA-XXXXXXXX).");
      return;
      }
    }

    setLoading(true);
    trackFunnel("signup", "started", { role, learner_entry_intent: role === "learner" ? learnerEntryIntent : null });
    try {
      await axios.post(
        `${API_URL}/api/auth/register`,
        {
          email, password, name, role,
          learner_entry_intent: role === "learner"
            ? (inviteCode ? "has_instructor" : learnerEntryIntent || null)
            : null,
          referral_code: referralCode || null,
          invite_code: inviteCode || null,
          organisation_invite_code: organisationInviteCode || null,
          beta_code: betaCode.trim() ? betaCode.trim().toUpperCase() : null,
          accept_terms: true,
          accept_privacy: true,
          accept_cancellation: true,
          terms_version: "1.0",
        },
        { withCredentials: true }
      );
      trackFunnel("signup", "completed", { role, learner_entry_intent: role === "learner" ? learnerEntryIntent : null });
      // P1 fix (Feb 2026) — replace `window.location.href` hard-reload
      // with refreshUser() + react-router navigate(replace:true). The
      // hard-reload occasionally landed at /learn before the JWT cookie
      // was committed by the browser, bouncing the invited learner back
      // to /login. Refresh the AuthContext first so RoleRoute sees the
      // freshly-registered user on the next render.
      if (organisationInviteCode) {
        await refreshUser();
        navigate("/pricing?from=adi-invite", { replace: true });
      } else {
        await refreshUser();
        if (inviteCode) {
          navigate("/learn", { replace: true });
        } else if (role === "learner" && learnerEntryIntent === "seeking_instructor") {
          navigate("/marketplace", { replace: true });
        } else if (role === "learner" && learnerEntryIntent === "has_instructor") {
          navigate("/learn/connect", { replace: true });
        } else if (role === "learner" && learnerEntryIntent === "independent_for_now") {
          navigate("/home", { replace: true });
        } else {
          navigate("/dashboard", { replace: true });
        }
      }
    } catch (e) {
      const detail = String(e?.response?.data?.detail || "");
      if (organisationInviteCode && detail.toLowerCase().includes("already registered")) {
        setError("This invitation belongs to your existing NDG account. Sign in below to accept it safely.");
      } else {
        setError(formatApiError(e, "Registration failed. Please try again."));
      }
    }
    setLoading(false);
  };

  const roleOptions = [
    {
      value: "learner",
      label: "Learner Driver",
      description: "I'm learning to drive",
      icon: <Car className="w-5 h-5 text-blue-600" />,
    },
    {
      value: "pdi",
      label: "PDI (Trainee Instructor)",
      description: "Training to become an instructor",
      icon: <GraduationCap className="w-5 h-5 text-purple-600" />,
    },
    {
      value: "adi",
      label: "ADI (Qualified Instructor)",
      description: "Already an instructor",
      icon: <Users className="w-5 h-5 text-emerald-600" />,
    },
  ];

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="px-4 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/" className="text-slate-600 hover:text-slate-900">
            <ArrowLeft className="w-6 h-6" />
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <Car className="w-5 h-5 text-white" />
            </div>
          </div>
        </div>
        <DarkModeToggle />
      </header>

      {/* Form */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 shadow-sm">
            <h1
              data-testid="register-title"
              className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mb-2"
            >
              Let's get you set up
            </h1>
            <p className="text-slate-600 mb-6">
              Takes about a minute.
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Referral Banner */}
              {referrerName && (
                <div data-testid="referral-banner" className="bg-purple-50 border border-purple-200 rounded-xl p-3 flex items-center gap-3">
                  <Gift className="w-5 h-5 text-purple-500 flex-shrink-0" />
                  <p className="text-sm text-purple-700">
                    Referred by <strong>{referrerName}</strong>
                  </p>
                </div>
              )}

              {error && (
                <div
                  data-testid="register-error"
                  className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm"
                >
                  {error}
                </div>
              )}

              {privateMode && !inviteCode && (
                organisationInviteCode ? null : (
                <div data-testid="register-beta-code-block" className="space-y-2 bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <Label htmlFor="beta_code" className="text-amber-900 font-bold flex items-center gap-2 text-sm">
                    Private beta — invite code required
                  </Label>
                  <p className="text-xs text-amber-700 leading-relaxed">
                    Sign-ups are paused while we onboard our first cohort. Enter your invite code below.
                  </p>
                  <Input
                    id="beta_code"
                    data-testid="register-beta-code-input"
                    type="text"
                    value={betaCode}
                    onChange={(e) => setBetaCode(e.target.value.toUpperCase())}
                    placeholder="BETA-XXXXXXXX"
                    autoCapitalize="characters"
                    spellCheck={false}
                    required
                    className="h-12 rounded-xl bg-white border-amber-300 focus:bg-white font-mono"
                  />
                </div>
                )
              )}

              {/* Bug fix (Feb 2026) — when an instructor-issued
                  invite_code is present we render a calm confirmation
                  panel INSTEAD of the BETA-XXXXXXXX block. The invite
                  IS the access authority; routing the user back into
                  the public beta gate would violate the Invite
                  Authority > Beta Gate doctrine. */}
              {privateMode && inviteCode && (
                <div
                  data-testid="register-invite-authority-block"
                  className="space-y-1 bg-emerald-50 border border-emerald-200 rounded-xl p-4"
                >
                  <p className="text-emerald-900 font-bold text-sm flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    Instructor invite verified
                  </p>
                  <p className="text-xs text-emerald-700 leading-relaxed">
                    You don't need a separate beta code — your
                    instructor's invite is your access.
                  </p>
                </div>
              )}

              {organisationInviteCode && (
                <div
                  data-testid="register-pdi-invite-authority-block"
                  className="space-y-1 bg-indigo-50 border border-indigo-200 rounded-xl p-4"
                >
                  <p className="text-indigo-950 font-bold text-sm flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    PDI invitation verified
                  </p>
                  <p className="text-xs text-indigo-800 leading-relaxed">
                    {organisationInvite?.organisation_name || "Your supervising ADI"} invited you.
                    {organisationInvite?.account_exists
                      ? " Sign in to your existing NDG account to accept, then choose and pay for your own plan."
                      : " Create your PDI account, then choose and pay for your own plan."}
                  </p>
                </div>
              )}

              {organisationInviteCode && organisationInvite?.account_exists && (
                <div
                  data-testid="register-existing-account-invite"
                  className="space-y-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4"
                >
                  <p className="text-sm font-bold text-emerald-950">Your NDG account is ready</p>
                  <p className="text-xs leading-relaxed text-emerald-800">
                    This email already belongs to your NDG account. Sign in to accept the PDI invitation
                    without creating a duplicate account or losing your existing ADI records.
                  </p>
                  <Link
                    data-testid="register-existing-account-sign-in"
                    to={`/login?organisation_invite=${encodeURIComponent(organisationInviteCode)}`}
                    className="flex h-12 w-full items-center justify-center rounded-xl bg-emerald-700 px-4 text-sm font-extrabold text-white transition hover:bg-emerald-800"
                  >
                    Sign in and accept invitation
                  </Link>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-700 font-medium">
                  Your name
                </Label>
                <Input
                  id="name"
                  data-testid="register-name-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Smith"
                  required
                  className="h-12 rounded-xl bg-slate-50 border-slate-200 focus:bg-white"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700 font-medium">
                  Email
                </Label>
                <Input
                  id="email"
                  data-testid="register-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  readOnly={!!organisationInviteCode}
                  placeholder="you@example.com"
                  required
                  className="h-12 rounded-xl bg-slate-50 border-slate-200 focus:bg-white"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="role" className="text-slate-700 font-medium">
                  I am a...
                </Label>
                <Select
                  value={role}
                  onValueChange={setRole}
                  disabled={!!organisationInviteCode || !!inviteCode}
                >
                  <SelectTrigger
                    data-testid="register-role-select"
                    className="h-12 rounded-xl bg-slate-50 border-slate-200"
                  >
                    <SelectValue placeholder="Select your role" />
                  </SelectTrigger>
                  <SelectContent>
                    {roleOptions.map((option) => (
                      <SelectItem
                        key={option.value}
                        value={option.value}
                        data-testid={`role-option-${option.value}`}
                      >
                        <div className="flex items-center gap-3">
                          {option.icon}
                          <div>
                            <p className="font-medium">{option.label}</p>
                            <p className="text-xs text-slate-500">{option.description}</p>
                          </div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {role === "learner" && !inviteCode && (
                <div className="space-y-2">
                  <Label htmlFor="learner_entry_intent" className="text-slate-700 font-medium">
                    How are you learning right now?
                  </Label>
                  <Select value={learnerEntryIntent} onValueChange={setLearnerEntryIntent}>
                    <SelectTrigger
                      id="learner_entry_intent"
                      data-testid="register-learner-entry-intent"
                      className="h-12 rounded-xl bg-slate-50 border-slate-200"
                    >
                      <SelectValue placeholder="Choose one" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="has_instructor" data-testid="learner-entry-has-instructor">
                        I already have a driving instructor
                      </SelectItem>
                      <SelectItem value="seeking_instructor" data-testid="learner-entry-seeking-instructor">
                        I want to find a driving instructor
                      </SelectItem>
                      <SelectItem value="independent_for_now" data-testid="learner-entry-independent">
                        I want to learn independently for now
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    If you find or connect an instructor later, your learning history comes with you.
                  </p>
                </div>
              )}

              {role === "learner" && inviteCode && (
                <div
                  data-testid="register-linked-learner-path"
                  className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3"
                >
                  <p className="text-sm font-semibold text-emerald-900">
                    You’re joining as a learner with your instructor.
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700 font-medium">
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    data-testid="register-password-input"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 10 characters"
                    required
                    className="h-12 rounded-xl bg-slate-50 border-slate-200 focus:bg-white pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Referral Code (optional) */}
              {!urlReferralCode && (
                <div className="space-y-2">
                  <Label htmlFor="referral" className="text-slate-700 font-medium">
                    Referral Code <span className="text-slate-400 font-normal">(optional)</span>
                  </Label>
                  <Input
                    id="referral"
                    data-testid="register-referral-input"
                    type="text"
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                    placeholder="e.g. JOHN-AB12C"
                    className="h-12 rounded-xl bg-slate-50 border-slate-200 focus:bg-white font-mono tracking-wider"
                  />
                </div>
              )}

              {/* ── Policy Acceptance Checkboxes ── */}
              <div className="space-y-3 pt-2 border-t border-slate-100">
                <p className="text-xs text-slate-500 font-medium pt-1">To create your account, please accept the following:</p>

                <PolicyCheckbox
                  testId="accept-terms"
                  checked={acceptTerms}
                  onChange={setAcceptTerms}
                  label={<>I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 font-semibold underline inline-flex items-center gap-0.5">Terms & Conditions<ExternalLink className="w-3 h-3" /></a></>}
                />
                <PolicyCheckbox
                  testId="accept-privacy"
                  checked={acceptPrivacy}
                  onChange={setAcceptPrivacy}
                  label={<>I agree to the <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 font-semibold underline inline-flex items-center gap-0.5">Privacy Policy<ExternalLink className="w-3 h-3" /></a></>}
                />
                <PolicyCheckbox
                  testId="accept-cancellation"
                  checked={acceptCancellation}
                  onChange={setAcceptCancellation}
                  label="I understand the 72-hour cancellation policy"
                />
              </div>

              <Button
                type="submit"
                data-testid="register-submit-btn"
                disabled={loading || !acceptTerms || !acceptPrivacy || !acceptCancellation}
                className={`w-full h-12 font-bold rounded-xl shadow-md transition-all
                  ${acceptTerms && acceptPrivacy && acceptCancellation
                    ? "bg-blue-700 hover:bg-blue-800 text-white shadow-blue-700/20"
                    : "bg-slate-300 text-slate-500 cursor-not-allowed shadow-none"
                  }`}
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                    Setting you up...
                  </span>
                ) : (
                  "Create my account"
                )}
              </Button>
            </form>

            <p className="mt-6 text-center text-slate-600">
              Already with us?{" "}
              <Link
                to="/login"
                data-testid="login-link"
                className="text-blue-600 hover:text-blue-700 font-semibold"
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function PolicyCheckbox({ testId, checked, onChange, label }) {
  return (
    <label data-testid={`${testId}-label`} className="flex items-start gap-3 cursor-pointer group">
      <div className="relative mt-0.5 flex-shrink-0">
        <input data-testid={testId} type="checkbox" checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="sr-only" />
        <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all
          ${checked
            ? "bg-blue-600 border-blue-600"
            : "border-slate-300 group-hover:border-blue-400"
          }`}>
          {checked && <Check className="w-3.5 h-3.5 text-white" />}
        </div>
      </div>
      <span className="text-xs text-slate-600 leading-relaxed">{label}</span>
    </label>
  );
}